import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LIBRARY_IMAGE } from "@/lib/content";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      navigate(from || (user.role === "admin" ? "/admin" : "/portal"), { replace: true });
    }
  }, [user, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      const dest = res.user.role === "admin" ? "/admin" : "/portal";
      // Only honor `from` when it matches the user's role-appropriate area
      const safeFrom = from && (
        (res.user.role === "admin" && from.startsWith("/admin")) ||
        (res.user.role !== "admin" && from.startsWith("/portal"))
      ) ? from : dest;
      navigate(safeFrom, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 bg-ivory grid grid-cols-1 lg:grid-cols-12" data-testid="login-page">
      <aside className="hidden lg:block lg:col-span-5 panel-navy relative overflow-hidden">
        <img src={LIBRARY_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="relative h-full flex flex-col justify-end p-16 text-ivory">
          <div className="kicker kicker-ivory">Client Portal</div>
          <h2 className="mt-6 font-serif text-5xl leading-[1.0]">
            The firm's <span className="italic text-brass">private rooms.</span>
          </h2>
          <p className="mt-6 text-ivory/70 font-light leading-relaxed max-w-sm">
            Sign in to review your engagement status, correspondence, and the particulars of any
            matter currently before the firm.
          </p>
        </div>
      </aside>

      <div className="lg:col-span-6 lg:col-start-7 flex items-center px-6 md:px-12 py-20">
        <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="login-form">
          <div className="kicker">Sign In</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.98]">
            Welcome,<br/>
            <span className="italic text-oxblood">counselor.</span>
          </h1>

          <div className="mt-12 space-y-8">
            <label className="block">
              <div className="field-label mb-2">Email</div>
              <input className="field" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </label>
            <label className="block">
              <div className="field-label mb-2">Password</div>
              <input className="field" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </label>
          </div>

          {error && (
            <div className="mt-6 text-oxblood font-serif italic text-sm" data-testid="login-error">— {error}</div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-40" data-testid="login-submit">
              {loading ? "Signing In…" : "Enter Portal"}
            </button>
            <Link to="/register" className="link-underline font-serif italic" data-testid="login-register-link">
              Open an account
            </Link>
          </div>

          <div className="mt-6 text-sm">
            <Link
              to="/forgot-password"
              className="text-slate2 hover:text-oxblood font-cormorant italic border-b border-slate2/30 hover:border-oxblood pb-0.5 transition-colors"
              data-testid="login-forgot-link"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="mt-12 hairline pt-6 text-xs uppercase tracking-[0.2em] text-slate2/70">
            All communications protected by attorney–client privilege
          </div>
        </form>
      </div>
    </div>
  );
}
